import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, betaDeleteGmailLabels } from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import { LabelModel } from '../../../../../../types';
import SharedDialog from '../../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import { Messages } from '../../../../../utils/message-constants';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteLabelsDialogProps {
  open: boolean;
  handleClose: () => void;
  onDeleteSuccess: () => void;
  userEmail: string;
  selectedRows: string[];
  userLabels: LabelModel[];
  changeLogRoles: string[];
}

export default function DeleteLabelsDialog({
  open,
  handleClose,
  onDeleteSuccess,
  userEmail,
  selectedRows,
  userLabels,
  changeLogRoles,
}: DeleteLabelsDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      betaDeleteGmailLabels(userEmail, selectedLabels, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted(
          'Label' + (selectedRows.length > 1 ? 's' : '')
        ),
      });

      const newList = userLabels.filter(
        (label) => !selectedRows.includes(label.id)
      );

      queryClient.setQueryData([UserKeys.GmailLabels, userEmail], newList);
      setChangeLogMessage('');
      onDeleteSuccess();
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          'the selected label' + (selectedRows.length > 1 ? 's' : '')
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .delete_gmail_labels,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.labels,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.DeleteGmailLabel.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const selectedLabels = userLabels.filter((label) =>
    selectedRows.includes(label.id)
  );

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={
        selectedLabels.length > 1 ? 'Delete Gmail Labels' : 'Delete Gmail Label'
      }
      dialogContent={
        <Box>
          <Typography sx={{ mb: 2 }}>
            {selectedLabels.length > 1 ? (
              <>
                Delete <strong>{selectedLabels.length} labels</strong> from{' '}
                <strong>{userEmail}'s</strong> Gmail labels?
              </>
            ) : (
              <>
                Delete Gmail label <strong>{selectedLabels[0]?.name}</strong>{' '}
                from <strong>{userEmail}'s</strong> Gmail labels?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: selectedLabels.length > 1 ? 'Delete Labels' : 'Delete Label',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
